export type CalendarEventsPdfFieldKey =
  | 'title'
  | 'status'
  | 'time'
  | 'room'
  | 'staff'
  | 'contact'
  | 'notes';

export type CalendarEventsPdfTheme = {
  headerBackground: string;
  headerText: string;
  tableHeaderBackground: string;
  tableHeaderText: string;
  rowAlternateBackground: string;
  borderColor: string;
  fields: Record<CalendarEventsPdfFieldKey, string>;
  columnVisibility: Record<CalendarEventsPdfFieldKey, boolean>;
};

export const CALENDAR_EVENTS_PDF_FIELD_KEYS: CalendarEventsPdfFieldKey[] = [
  'title',
  'status',
  'time',
  'room',
  'staff',
  'contact',
  'notes',
];

export const DEFAULT_CALENDAR_EVENTS_PDF_THEME: CalendarEventsPdfTheme = {
  headerBackground: '#1d4ed8',
  headerText: '#ffffff',
  tableHeaderBackground: '#e8f0fe',
  tableHeaderText: '#1e3a5f',
  rowAlternateBackground: '#f8fafc',
  borderColor: '#cbd5e1',
  fields: {
    title: '#0f172a',
    status: '#047857',
    time: '#334155',
    room: '#0f766e',
    staff: '#1d4ed8',
    contact: '#9a3412',
    notes: '#475569',
  },
  columnVisibility: CALENDAR_EVENTS_PDF_FIELD_KEYS.reduce<Record<CalendarEventsPdfFieldKey, boolean>>((acc, key) => {
    acc[key] = true;
    return acc;
  }, {} as Record<CalendarEventsPdfFieldKey, boolean>),
};

const STORAGE_KEY = 'shahagent.calendar-events-pdf-theme';

const isHexColor = (value: string) => /^#[0-9a-fA-F]{6}$/.test(value);

const sanitizeColor = (value: unknown, fallback: string) => (
  typeof value === 'string' && isHexColor(value) ? value : fallback
);

export const hexToRgb = (hex: string): [number, number, number] => {
  const normalized = hex.replace('#', '');
  const value = Number.parseInt(normalized, 16);

  return [
    (value >> 16) & 255,
    (value >> 8) & 255,
    value & 255,
  ];
};

export const loadCalendarEventsPdfTheme = (): CalendarEventsPdfTheme => {
  if (typeof window === 'undefined') {
    return DEFAULT_CALENDAR_EVENTS_PDF_THEME;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_CALENDAR_EVENTS_PDF_THEME;
    }

    const parsed = JSON.parse(raw) as Partial<CalendarEventsPdfTheme>;
    const defaults = DEFAULT_CALENDAR_EVENTS_PDF_THEME;

    return {
      headerBackground: sanitizeColor(parsed.headerBackground, defaults.headerBackground),
      headerText: sanitizeColor(parsed.headerText, defaults.headerText),
      tableHeaderBackground: sanitizeColor(parsed.tableHeaderBackground, defaults.tableHeaderBackground),
      tableHeaderText: sanitizeColor(parsed.tableHeaderText, defaults.tableHeaderText),
      rowAlternateBackground: sanitizeColor(parsed.rowAlternateBackground, defaults.rowAlternateBackground),
      borderColor: sanitizeColor(parsed.borderColor, defaults.borderColor),
      fields: CALENDAR_EVENTS_PDF_FIELD_KEYS.reduce<Record<CalendarEventsPdfFieldKey, string>>((acc, key) => {
        acc[key] = sanitizeColor(parsed.fields?.[key], defaults.fields[key]);
        return acc;
      }, {} as Record<CalendarEventsPdfFieldKey, string>),
      columnVisibility: CALENDAR_EVENTS_PDF_FIELD_KEYS.reduce<Record<CalendarEventsPdfFieldKey, boolean>>((acc, key) => {
        const value = parsed.columnVisibility?.[key];
        acc[key] = typeof value === 'boolean' ? value : defaults.columnVisibility[key];
        return acc;
      }, {} as Record<CalendarEventsPdfFieldKey, boolean>),
    };
  } catch {
    return DEFAULT_CALENDAR_EVENTS_PDF_THEME;
  }
};

export const saveCalendarEventsPdfTheme = (theme: CalendarEventsPdfTheme) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
};
